import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getMPData } from 'src/mentor/services'
import { refreshCheckIntoRoom } from './services'

/**
 * BroadcastChannel for cross-tab communication
 * Allows multiple tabs to sync check-in state
 */
let checkInChannel = null

/**
 * Initialize BroadcastChannel for check-in sync
 * @returns {BroadcastChannel|null} The channel instance or null if not supported
 */
export function initCheckInBroadcastChannel() {
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('[CheckInSync] BroadcastChannel not supported in this browser')
    return null
  }

  if (!checkInChannel) {
    checkInChannel = new BroadcastChannel('check-in-sync')
    
    // Listen for messages from other tabs
    checkInChannel.onmessage = (event) => {
      const { type, data } = event.data
      
      
      switch (type) {
        case 'check-in-updated':
          if (data) {
            localStorage.setItem('checkedInDetails', JSON.stringify(data))
            // Also update peekedInRoomDetails if provided
            if (data.chamberRoom) {
              localStorage.setItem('peekedInRoomDetails', JSON.stringify(data.chamberRoom))
            }
            // Dispatch event for components to react
            window.dispatchEvent(
              new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedIn, {
                detail: {
                  roomData: data.chamberRoom,
                  usageLog: data,
                },
              }),
            )
          }
          break
          
        case 'check-in-removed':
          localStorage.removeItem('checkedInDetails')
          localStorage.removeItem('peekedInRoomDetails')
          localStorage.removeItem('roomDataEventTypes')
          // Dispatch event for components to react
          window.dispatchEvent(new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, { detail: {} }))
          break
          
        case 'check-in-validated':
          // Another tab validated, update our timestamp
          if (data?.lastValidated) {
            const current = localStorage.getItem('checkedInDetails')
            if (current) {
              try {
                const parsed = JSON.parse(current)
                parsed.lastValidated = data.lastValidated
                localStorage.setItem('checkedInDetails', JSON.stringify(parsed))
              } catch (e) {
                console.error('[CheckInSync] Error updating validation timestamp:', e)
              }
            }
          }
          break
          
        default:
          console.warn('[CheckInSync] Unknown message type:', type)
      }
    }
  }
  
  return checkInChannel
}

/**
 * Broadcast check-in update to other tabs
 * @param {Object} checkInData - The check-in data to broadcast
 * @param {Object} chamberRoom - Optional chamber room data
 */
export function broadcastCheckInUpdate(checkInData, chamberRoom = null) {
  if (!checkInChannel) {
    initCheckInBroadcastChannel()
  }
  
  if (checkInChannel) {
    checkInChannel.postMessage({
      type: 'check-in-updated',
      data: {
        ...checkInData,
        chamberRoom,
        lastValidated: new Date().toISOString(),
      },
    })
  }
}

/**
 * Broadcast check-in removal to other tabs
 */
export function broadcastCheckInRemoval() {
  if (!checkInChannel) {
    initCheckInBroadcastChannel()
  }
  
  if (checkInChannel) {
    checkInChannel.postMessage({
      type: 'check-in-removed',
    })
  }
}

/**
 * Broadcast validation timestamp to other tabs
 */
export function broadcastValidationTimestamp() {
  if (!checkInChannel) {
    initCheckInBroadcastChannel()
  }
  
  if (checkInChannel) {
    checkInChannel.postMessage({
      type: 'check-in-validated',
      data: {
        lastValidated: new Date().toISOString(),
      },
    })
  }
}

/**
 * Maximum age for check-in data before it's considered stale (5 minutes)
 */
const MAX_STALE_TIME_MS = 5 * 60 * 1000

/**
 * Check if check-in data is stale
 * @param {Object} checkedInDetails - The check-in details object
 * @returns {boolean} True if data is stale
 */
export function isCheckInStale(checkedInDetails) {
  if (!checkedInDetails) return true
  
  const lastValidated = checkedInDetails?.lastValidated
  if (!lastValidated) return true
  
  try {
    const lastValidatedDate = new Date(lastValidated)
    const now = new Date()
    const age = now - lastValidatedDate
    
    return age > MAX_STALE_TIME_MS
  } catch (e) {
    console.error('[CheckInSync] Error checking stale data:', e)
    return true
  }
}

/**
 * Retry configuration for resilient server calls
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504], // Timeout, rate limit, server errors
  retryableErrors: ['ECONNABORTED', 'ETIMEDOUT', 'ENOTFOUND', 'ECONNRESET'],
}

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Check if error is retryable
 * @param {Error|Object} error - The error to check
 * @returns {boolean} True if error is retryable
 */
function isRetryableError(error) {
  // Check for network errors
  if (error?.code && RETRY_CONFIG.retryableErrors.includes(error.code)) {
    return true
  }
  
  // Check for axios timeout/abort errors
  if (error?.message) {
    const message = error.message.toLowerCase()
    if (message.includes('timeout') || message.includes('network error') || message.includes('aborted')) {
      return true
    }
  }
  
  // Check for response status codes
  if (error?.response?.status) {
    return RETRY_CONFIG.retryableStatusCodes.includes(error.response.status)
  }
  
  // Check for axios error types
  if (error?.isAxiosError) {
    // Network errors are retryable
    if (!error.response) {
      return true
    }
    // Server errors are retryable
    if (error.response.status >= 500) {
      return true
    }
  }
  
  return false
}

/**
 * Calculate delay for retry attempt with exponential backoff
 * @param {number} attemptNumber - Current attempt number (0-indexed)
 * @returns {number} Delay in milliseconds
 */
function calculateRetryDelay(attemptNumber) {
  const delay = RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attemptNumber)
  return Math.min(delay, RETRY_CONFIG.maxDelay)
}

/**
 * Fetch current check-in status from server with retry logic
 * @param {number} attemptNumber - Current attempt number (for retries)
 * @returns {Promise<Object|null>} Check-in data or null if not checked in
 */
export async function fetchCurrentCheckInStatus(attemptNumber = 0) {
  try {
    // Try to get check-in status from server
    // Note: This endpoint may need to be created on the server
    // For now, we'll use the refresh endpoint which returns current status
    const localCheckIn = localStorage.getItem('checkedInDetails')
    
    if (!localCheckIn) {
      return null
    }
    
    const parsedLocal = JSON.parse(localCheckIn)
    
    
    // Use refresh endpoint to validate current status
    const response = await refreshCheckIntoRoom({
      selectedCheckInEventOption: parsedLocal?.selectedCheckInEventOption,
      clinicBrandId: parsedLocal?.clinicBrandId,
      clinicLocationId: parsedLocal?.clinicLocationId,
      checkinMethod: parsedLocal?.checkinMethod,
      chamberRoomId: parsedLocal?.chamberRoomId,
      apiParam: 'emr-usage-log',
    })
    
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const emrUsageLog = response?.data?.data?.['emr-usage-log']
      const chamberRoom = response?.data?.data?.['chamber-room']
      
      if (emrUsageLog) {
        return {
          emrUsageLog: {
            ...emrUsageLog,
            lastValidated: new Date().toISOString(),
          },
          chamberRoom,
        }
      }
    }
    
    // If refresh fails with non-retryable error, check-in is no longer valid
    return null
  } catch (error) {
    console.error(`[CheckInSync] Error fetching current check-in status (attempt ${attemptNumber + 1}):`, error)
    
    // Check if error is retryable and we haven't exceeded max retries
    if (isRetryableError(error) && attemptNumber < RETRY_CONFIG.maxRetries) {
      const delay = calculateRetryDelay(attemptNumber)
      
      await sleep(delay)
      
      // Retry with incremented attempt number
      return fetchCurrentCheckInStatus(attemptNumber + 1)
    }
    
    // Non-retryable error or max retries exceeded
    if (attemptNumber >= RETRY_CONFIG.maxRetries) {
      console.error('[CheckInSync] Max retries exceeded, giving up')
    } else {
      console.error('[CheckInSync] Non-retryable error, not retrying')
    }
    
    // On error, return null to indicate we couldn't validate
    // This will trigger a re-check later
    return null
  }
}

/**
 * Validate and sync check-in status with server
 * This should be called on page load to ensure localStorage is in sync
 * Uses resilient retry logic for server calls
 * @returns {Promise<Object>} Result object with sync status
 */
export async function validateAndSyncCheckIn() {
  
  const localCheckInStr = localStorage.getItem('checkedInDetails')
  const localCheckIn = localCheckInStr ? JSON.parse(localCheckInStr) : null
  
  // If no local check-in, nothing to validate
  if (!localCheckIn) {
    return {
      synced: true,
      action: 'none',
      reason: 'no-local-check-in',
    }
  }
  
  // Check if data is stale
  const isStale = isCheckInStale(localCheckIn)
  
  
  // Track validation attempts
  let lastError = null
  let serverCheckIn = null
  
  // Try to fetch from server with retry logic (handled inside fetchCurrentCheckInStatus)
  try {
    serverCheckIn = await fetchCurrentCheckInStatus()
  } catch (error) {
    console.error('[CheckInSync] Error during server fetch (after retries):', error)
    lastError = error
  }
  
  // Handle server response
  if (serverCheckIn) {
    // Server has check-in - update localStorage with server data
    const updatedCheckIn = {
      ...serverCheckIn.emrUsageLog,
      lastValidated: new Date().toISOString(),
      validationError: false, // Clear any previous validation errors
    }
    
    localStorage.setItem('checkedInDetails', JSON.stringify(updatedCheckIn))
    
    if (serverCheckIn.chamberRoom) {
      localStorage.setItem('peekedInRoomDetails', JSON.stringify(serverCheckIn.chamberRoom))
    }
    
    // Broadcast update to other tabs
    broadcastCheckInUpdate(updatedCheckIn, serverCheckIn.chamberRoom)
    
    // Check if data actually changed
    const dataChanged = JSON.stringify(localCheckIn) !== JSON.stringify(updatedCheckIn)
    
    
    return {
      synced: true,
      action: dataChanged ? 'updated' : 'validated',
      reason: isStale ? 'stale-data-refreshed' : 'validation-success',
      dataChanged,
    }
  } else if (lastError || !serverCheckIn) {
    // Server fetch failed or returned null
    // This could mean:
    // 1. Server error (network issues, etc.) - keep local data
    // 2. Server says no check-in - remove local data
    
    // If we got a server response (not a network error), it means check-in is invalid
    const isNetworkError = lastError && (
      lastError.code === 'ECONNABORTED' ||
      lastError.code === 'ETIMEDOUT' ||
      lastError.code === 'ENOTFOUND' ||
      lastError.code === 'ECONNRESET' ||
      !lastError.response
    )
    
    if (isNetworkError) {
      // Network error - keep local data but mark as potentially stale
      console.warn('[CheckInSync] Network error during validation, keeping local data but marking as potentially stale')
      
      const updatedCheckIn = {
        ...localCheckIn,
        lastValidated: new Date().toISOString(),
        validationError: true, // Flag that validation failed
        validationErrorReason: 'network-error',
      }
      localStorage.setItem('checkedInDetails', JSON.stringify(updatedCheckIn))
      
      return {
        synced: false,
        action: 'kept-local',
        reason: 'network-error',
        error: lastError?.message || 'Network error',
        keptLocalData: true,
      }
    } else {
      // Server responded but said no check-in - remove local data
      console.warn('[CheckInSync] Server has no check-in, but local does - removing local check-in')
      
      localStorage.removeItem('checkedInDetails')
      localStorage.removeItem('peekedInRoomDetails')
      localStorage.removeItem('roomDataEventTypes')
      
      // Broadcast removal to other tabs
      broadcastCheckInRemoval()
      
      // Dispatch event for components
      window.dispatchEvent(new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, { detail: {} }))
      
      return {
        synced: true,
        action: 'removed',
        reason: 'server-no-check-in',
      }
    }
  }
  
  // Fallback (shouldn't reach here)
  return {
    synced: false,
    action: 'none',
    reason: 'unknown-error',
    error: 'Unexpected validation state',
  }
}

/**
 * Update check-in data with validation timestamp
 * @param {Object} checkInData - The check-in data to update
 */
export function updateCheckInWithValidation(checkInData) {
  const updated = {
    ...checkInData,
    lastValidated: new Date().toISOString(),
  }
  localStorage.setItem('checkedInDetails', JSON.stringify(updated))
  broadcastCheckInUpdate(updated)
  broadcastValidationTimestamp()
}

/**
 * Cleanup BroadcastChannel
 */
export function cleanupCheckInBroadcastChannel() {
  if (checkInChannel) {
    checkInChannel.close()
    checkInChannel = null
  }
}

